// Request Metrics types
interface RequestMetricsConfig {
  token: string;
  environment?: string;
  [key: string]: any;
}

// Extend window interface for Request Metrics
declare global {
  interface Window {
    RM?: {
      install: (config: RequestMetricsConfig) => void;
      identify: (userId: string, options?: any) => void;
      sendEvent: (eventName: string, metadata?: any) => void;
      setUrlGroup: (urlGroup: string) => void;
      track: (error: any, metadata?: any) => void;
      addMetadata: (metadata: any) => void;
      _events: any[];
      _errors: any[];
      _metadata: any;
      _urlGroup: string | null;
      _userId: string;
      _identifyOptions: any;
      _options: RequestMetricsConfig;
    };
  }
}

/**
 * Initialize Request Metrics
 */
export function initializeRequestMetrics(config?: any): void {
  try {
    // Only load in specific environments (similar to Sentry pattern)
    const allowedEnvs = ['production'];
    if (!allowedEnvs.includes(config?.mode)) {
      console.log(`[Request Metrics] Skipping initialization in ${config?.mode || 'development'} mode`);
      return;
    }

    // Check if Request Metrics is already loaded
    if (window.RM) {
      console.log('[Request Metrics] Already initialized, skipping');
      return;
    }

    // Initialize Request Metrics with environment-specific config
    const rmConfig: RequestMetricsConfig = {
      token: "i9yd7zv:r6vf8fj",
      environment: config.mode,
    };

    // Load Request Metrics script
    loadRequestMetricsScript(rmConfig);
    
    console.log(`[Request Metrics] Initialized successfully in ${config.mode} mode`);
  } catch (error) {
    console.error('[Request Metrics] Initialization failed:', error);
  }
}

/**
 * Load Request Metrics script dynamically
 */
function loadRequestMetricsScript(config: RequestMetricsConfig): void {
  // Create the Request Metrics initialization script
  const script = document.createElement('script');
  script.textContent = `
    (function(t,e,n,r){
      function a(){return e&&e.now?e.now():null}
      if(!n.version){
        n._events=[];
        n._errors=[];
        n._metadata={};
        n._urlGroup=null;
        window.RM=n;
        n.install=function(e){
          n._options=e;
          var a=t.createElement("script");
          a.async=true;
          a.crossOrigin="anonymous";
          a.src=r;
          var o=t.getElementsByTagName("script")[0];
          o.parentNode.insertBefore(a,o)
        };
        n.identify=function(t,e){
          n._userId=t;
          n._identifyOptions=e
        };
        n.sendEvent=function(t,e){
          n._events.push({eventName:t,metadata:e,time:a()})
        };
        n.setUrlGroup=function(t){
          n._urlGroup=t
        };
        n.track=function(t,e){
          n._errors.push({error:t,metadata:e,time:a()})
        };
        n.addMetadata=function(t){
          n._metadata=Object.assign(n._metadata,t)
        }
      }
    })(document,window.performance,window.RM||{},"https://cdn.requestmetrics.com/agent/current/rm.js");
    
    // Install with configuration
    if(window.RM) {
      window.RM.install(${JSON.stringify(config)});
    }
  `;
  
  // Insert the script into the document head
  document.head.appendChild(script);
}

/**
 * Add custom context to Request Metrics
 */
export function addRequestMetricsContext(context: Record<string, any>): void {
  try {
    if (window.RM) {
      window.RM.addMetadata(context);
    }
  } catch (error) {
    console.error('[Request Metrics] Failed to add context:', error);
  }
}

/**
 * Track custom events in Request Metrics
 */
export function trackRequestMetricsEvent(eventName: string, metadata?: Record<string, any>): void {
  try {
    if (window.RM) {
      window.RM.sendEvent(eventName, metadata || {});
    }
  } catch (error) {
    console.error('[Request Metrics] Failed to track event:', error);
  }
}

/**
 * Set user information in Request Metrics
 * @param email - User email as unique identifier
 * @param options - Additional user data (userId, companyId, locationId, etc.)
 */
export function setRequestMetricsUser(email: string, options?: any): void {
  try {
    if (window.RM) {
      window.RM.identify(email, options);
    }
  } catch (error) {
    console.error('[Request Metrics] Failed to set user:', error);
  }
}

/**
 * Request Metrics metadata interface
 */
export interface RequestMetricsEvent {
  locale: string;
  email?: string;
  userId?: string;
  companyId?: string;
  locationId?: string;
}

/**
 * Set Request Metrics metadata similar to Sentry
 */
export function setRequestMetricsMeta(event: RequestMetricsEvent): void {
  try {
    if (!window.RM) {
      console.log('[Request Metrics] Not initialized, skipping metadata setting');
      return;
    }

    // Set user information using email as unique identifier
    if (event?.email) {
      const userData = {
        email: event.email,
        userId: event.userId,
        companyId: event.companyId,
        locationId: event.locationId
      };

      // Remove undefined values from user data
      const cleanUserData = Object.fromEntries(
        Object.entries(userData).filter(([_, value]) => value !== undefined)
      );

      window.RM.identify(event.email, cleanUserData);
    }

    // Add metadata for context (excluding user-specific data)
    const metadata = {
      locale: event.locale,
      timestamp: new Date().toISOString()
    };

    // Remove undefined values
    const cleanMetadata = Object.fromEntries(
      Object.entries(metadata).filter(([_, value]) => value !== undefined)
    );

    window.RM.addMetadata(cleanMetadata);

    console.log('[Request Metrics] User and metadata set successfully:', {
      userEmail: event.email,
      userData: event.email ? { email: event.email, userId: event.userId, companyId: event.companyId, locationId: event.locationId } : null,
      metadata: cleanMetadata
    });
  } catch (error) {
    console.error('[Request Metrics] Failed to set metadata:', error);
  }
}

/**
 * Track errors in Request Metrics
 */
export function trackRequestMetricsError(error: any, metadata?: Record<string, any>): void {
  try {
    if (window.RM) {
      window.RM.track(error, metadata || {});
    }
  } catch (error) {
    console.error('[Request Metrics] Failed to track error:', error);
  }
}

/**
 * Set URL group in Request Metrics
 */
export function setRequestMetricsUrlGroup(urlGroup: string): void {
  try {
    if (window.RM) {
      window.RM.setUrlGroup(urlGroup);
    }
  } catch (error) {
    console.error('[Request Metrics] Failed to set URL group:', error);
  }
}

/**
 * Test Request Metrics integration
 */
export function testRequestMetrics(): void {
  try {
    if (window.RM) {
      // Test user identification with email
      window.RM.identify('test@example.com', {
        userId: 'test-user-123',
        companyId: 'test-company-456',
        locationId: 'test-location-789'
      });

      // Test event tracking
      window.RM.sendEvent('test_event', {
        timestamp: new Date().toISOString(),
        test: true
      });

      // Test metadata
      window.RM.addMetadata({
        testMetadata: true,
        locale: 'en-US'
      });

      console.log('[Request Metrics] Test completed successfully - user identified, event sent, metadata added');
    } else {
      console.log('[Request Metrics] Not initialized, test skipped');
    }
  } catch (error) {
    console.error('[Request Metrics] Failed to test:', error);
  }
}

// Export all functionality as default
export default {
  initializeRequestMetrics,
  addRequestMetricsContext,
  trackRequestMetricsEvent,
  setRequestMetricsUser,
  setRequestMetricsMeta,
  trackRequestMetricsError,
  setRequestMetricsUrlGroup,
  testRequestMetrics
};
